import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import { FC } from 'react';

import { LeadMergeTagText } from './LeadMergeTagText';

type LeadNewsletterSendModalProps = {
  isOpen: boolean;
  recipientSummary: string;
  recipientCount: number;
  groupName?: string | null;
  subject: string;
  useHomecookTemplate: boolean;
  submitting?: boolean;
  onClose: () => void;
  onConfirm: () => void;
};

/** Styled confirmation modal before sending a newsletter campaign. */
export const LeadNewsletterSendModal: FC<LeadNewsletterSendModalProps> = ({
  isOpen,
  recipientSummary,
  recipientCount,
  groupName,
  subject,
  useHomecookTemplate,
  submitting = false,
  onClose,
  onConfirm,
}) => {
  if (!isOpen) return null;

  const targetLabel = groupName
    ? `saved group "${groupName}" (${recipientCount} lead${recipientCount === 1 ? '' : 's'})`
    : `current filtered view (${recipientCount} lead${recipientCount === 1 ? '' : 's'})`;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <button
        type="button"
        aria-label="Close send confirmation"
        className="absolute inset-0 bg-[#013847]/40 backdrop-blur-[1px]"
        onClick={() => !submitting && onClose()}
      />
      <dialog
        open
        aria-labelledby="lead-send-modal-title"
        className="relative z-10 w-full max-w-lg overflow-hidden rounded-2xl border border-[#E9E9E9] bg-white shadow-[0_16px_48px_rgba(1,56,71,0.18)]"
      >
        <div className="border-b border-[#E9E9E9] bg-gradient-to-r from-[#EFF8FA] to-white px-6 py-5">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-[#5DB6CE] font-raleway">
                Confirm send
              </p>
              <h2 id="lead-send-modal-title" className="mt-1 text-xl font-bold text-[#013847] font-raleway">
                Send newsletter campaign?
              </h2>
            </div>
            <button
              type="button"
              aria-label="Close"
              disabled={submitting}
              className="rounded-lg p-1 text-[#737373] hover:bg-[#F5F5F5] disabled:opacity-50"
              onClick={onClose}
            >
              <CloseIcon fontSize="small" />
            </button>
          </div>
        </div>

        <div className="space-y-4 px-6 py-5 font-open-sans text-sm text-[#2C3E50]">
          <div className="rounded-xl border border-[#CFE8EF] bg-[#EFF8FA] px-4 py-3">
            <p className="font-semibold text-[#013847]">Recipients</p>
            <p className="mt-1">{targetLabel}</p>
            <p className="mt-2 text-xs text-[#737373]">{recipientSummary}</p>
          </div>

          <div className="rounded-xl border border-[#E9E9E9] bg-[#FAFAFA] px-4 py-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-[#737373]">Subject</p>
            <p className="mt-1 font-medium">
              <LeadMergeTagText value={subject} fallback="(no subject)" />
            </p>
          </div>

          <p className="text-xs text-[#737373]">
            {useHomecookTemplate
              ? 'Message will be wrapped in the HomeCook email template (logo, header, footer).'
              : 'Message will be sent as custom HTML without the HomeCook wrapper.'}
          </p>
        </div>

        <div className="flex flex-wrap gap-3 border-t border-[#E9E9E9] bg-[#FAFAFA] px-6 py-4">
          <button
            type="button"
            disabled={submitting}
            className="flex-1 rounded-xl border border-[#E9E9E9] bg-white px-4 py-2.5 text-sm font-semibold text-[#013847] font-raleway disabled:opacity-60"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={submitting}
            data-testid="confirm-newsletter-send"
            className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl bg-[#013847] px-4 py-2.5 text-sm font-semibold text-white font-raleway disabled:opacity-60"
            onClick={onConfirm}
          >
            <SendIcon sx={{ fontSize: 18 }} />
            {submitting ? 'Sending…' : 'Send now'}
          </button>
        </div>
      </dialog>
    </div>
  );
};
